import axios, { type AxiosError } from 'axios';
import { useEffect } from 'react';
import { Provider } from 'react-redux';
import { registerApiErrorToast } from '../api/client';
import ToastContainer from '../components/ToastContainer';
import client from '../api/client';
import { initializeAuth, signOut } from './authSlice';
import { store } from './index';
import { useAppDispatch, useToasts } from './hooks';
import { loadInitialSyncStatus, setReachability, setupSyncEngine } from './syncSlice';
import { setupReachability, reachabilityMonitor } from '../offline/reachability';
import { setupCacheInvalidation } from './cacheInvalidationSetup';
import { showError, dismissToast } from './toastSlice';

function isAuthError(error: AxiosError): boolean {
  const status = error.response?.status;
  return status === 401 || status === 403;
}

function AppEffects() {
  const dispatch = useAppDispatch();
  const { toasts, dismissToast: onDismiss } = useToasts();

  useEffect(() => {
    void dispatch(initializeAuth());
  }, [dispatch]);

  useEffect(() => {
    const interceptor = client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (axios.isAxiosError(error) && isAuthError(error)) {
          void dispatch(signOut());
        }
        return Promise.reject(error);
      },
    );

    return () => {
      client.interceptors.response.eject(interceptor);
    };
  }, [dispatch]);

  useEffect(() => {
    const showErrorToast = (message: string) => {
      const action = showError(message);
      dispatch(action);
      window.setTimeout(() => dispatch(dismissToast(action.payload.id)), 5000);
    };
    registerApiErrorToast(showErrorToast);
    return () => registerApiErrorToast(null);
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    let unsubscribeSync: (() => void) | undefined;

    void loadInitialSyncStatus((action) => dispatch(action)).then((unsub) => {
      if (cancelled) {
        unsub();
        return;
      }
      unsubscribeSync = unsub;
    });

    const teardownAutoSync = setupSyncEngine();
    const teardownCacheInvalidation = setupCacheInvalidation();
    const teardownReachability = setupReachability();
    const unsubReachability = reachabilityMonitor.subscribe((snapshot) => {
      dispatch(
        setReachability({
          isReachable: snapshot.isReachable,
          isConfirmedOffline: snapshot.isConfirmedOffline,
        }),
      );
    });

    return () => {
      cancelled = true;
      unsubscribeSync?.();
      teardownAutoSync();
      teardownCacheInvalidation();
      teardownReachability();
      unsubReachability();
    };
  }, [dispatch]);

  return <ToastContainer toasts={toasts} onDismiss={onDismiss} />;
}

export function StoreProvider({ children }: { children: React.ReactNode }) {
  return (
    <Provider store={store}>
      {children}
      <AppEffects />
    </Provider>
  );
}
